import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  GraduationCap,
  ArrowRight,
  Award,
} from 'lucide-react';
import { useMyEnrollments } from '@/hooks/useCourses';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

type EnrollmentStatus = 'active' | 'completed';

const STATUS_VARIANTS: Record<EnrollmentStatus, 'blue' | 'green'> = {
  active: 'blue',
  completed: 'green',
};

const LEVEL_VARIANTS: Record<string, 'gray' | 'yellow' | 'red'> = {
  beginner: 'gray',
  intermediate: 'yellow',
  advanced: 'red',
};

export default function MyCourses() {
  const { t } = useTranslation('account');
  const { data, isLoading, isError } = useMyEnrollments();

  const enrollments = data?.enrollments ?? [];

  const statusBadge = useMemo(
    () => (status: string) => {
      const variant = STATUS_VARIANTS[status as EnrollmentStatus] ?? 'gray';
      const labelKey = `courses.status.${status}`;
      const label = t(labelKey, { defaultValue: status });
      return <Badge variant={variant}>{label}</Badge>;
    },
    [t],
  );

  const levelBadge = useMemo(
    () => (level: string) => {
      const variant = LEVEL_VARIANTS[level] ?? 'gray';
      const labelKey = `courses.level.${level}`;
      const label = t(labelKey, { defaultValue: level });
      return <Badge variant={variant}>{label}</Badge>;
    },
    [t],
  );

  if (isLoading) {
    return <LoadingSpinner className="py-16" />;
  }

  if (isError) {
    return (
      <div className="text-center py-16">
        <p className="text-ink-muted mb-4">{t('courses.errorLoading')}</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          {t('common.tryAgain')}
        </Button>
      </div>
    );
  }

  if (enrollments.length === 0) {
    return (
      <div className="text-center py-16">
        <BookOpen
          className="h-16 w-16 text-ink-muted mx-auto mb-4"
          aria-hidden="true"
        />
        <h2 className="text-xl font-display font-bold text-ink mb-2">
          {t('courses.emptyHeading')}
        </h2>
        <p className="text-ink-muted mb-6">{t('courses.emptyDescription')}</p>
        <Link to="/courses">
          <Button>{t('courses.browseCourses')}</Button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-display font-bold text-ink">
          {t('courses.heading')}
        </h2>
        <p className="text-sm text-ink-muted">
          {t('courses.totalCount', { count: enrollments.length })}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {enrollments.map((enrollment: any) => (
          <Card key={enrollment.id} padding="none" className="overflow-hidden">
            <div className="flex flex-col h-full">
              {/* Thumbnail / Icon header */}
              <div className="bg-surface-muted p-4 flex items-center gap-4">
                {enrollment.course?.thumbnail_url ? (
                  <img
                    src={enrollment.course.thumbnail_url}
                    alt=""
                    className="h-16 w-16 rounded-lg object-cover shrink-0"
                  />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-sage-100 shrink-0">
                    <GraduationCap
                      className="h-8 w-8 text-sage-700"
                      aria-hidden="true"
                    />
                  </div>
                )}
                <div className="min-w-0">
                  <h3 className="font-semibold text-ink truncate">
                    {enrollment.course?.title ?? t('courses.untitled')}
                  </h3>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {enrollment.course?.level && levelBadge(enrollment.course.level)}
                    {statusBadge(enrollment.status)}
                  </div>
                </div>
              </div>

              {/* Progress and action */}
              <div className="p-4 flex flex-col gap-3 flex-1">
                {/* Progress bar */}
                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-ink-muted">{t('courses.progress')}</span>
                    <span className="font-medium text-ink">
                      {Math.round(enrollment.completion_percentage ?? 0)}%
                    </span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-line overflow-hidden">
                    <div
                      className="h-full rounded-full bg-sage-500 transition-all duration-300"
                      style={{
                        width: `${Math.min(100, enrollment.completion_percentage ?? 0)}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Action button */}
                <div className="mt-auto pt-1">
                  {enrollment.status === 'completed' ? (
                    <Link
                      to={`/account/courses/${enrollment.course_id}`}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-sage-700 hover:text-sage-800"
                    >
                      <Award className="h-4 w-4" aria-hidden="true" />
                      {t('courses.viewCertificate')}
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  ) : (
                    <Link to={`/account/courses/${enrollment.course_id}`}>
                      <Button size="sm" className="w-full">
                        {t('courses.continueLearning')}
                        <ArrowRight className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
